import { Box, Paper, Stack, Typography } from '@offline-mui/material'
import type { PaperProps } from '@offline-mui/material'
import type { ReactNode } from 'react'

type SectionCardProps = Omit<PaperProps, 'title'> & {
  title?: ReactNode
  subtitle?: ReactNode
  icon?: ReactNode
  action?: ReactNode
  disablePadding?: boolean
  children: ReactNode
}

/**
 * Card with an optional titled header row and a padded body. Used as the
 * primary content container across pages so every panel shares the same
 * spacing rhythm.
 */
export default function SectionCard({
  title,
  subtitle,
  icon,
  action,
  disablePadding = false,
  children,
  sx,
  ...paperProps
}: SectionCardProps) {
  const hasHeader = Boolean(title || subtitle || action)

  return (
    <Paper
      className="fcc-fade-up"
      {...paperProps}
      sx={[{ overflow: 'hidden' }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {hasHeader ? (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{
            px: { xs: 2, md: 3 },
            py: 2,
            alignItems: { sm: 'center' },
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
            {icon ? (
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                  bgcolor: (t) => t.palette.primary.main + '14',
                  color: 'primary.main',
                  '& svg': { fontSize: 20 },
                }}
              >
                {icon}
              </Box>
            ) : null}
            <Box sx={{ minWidth: 0 }}>
              {title ? (
                <Typography variant="h6" component="h2">
                  {title}
                </Typography>
              ) : null}
              {subtitle ? (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
          </Stack>
          {action ? (
            <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
              {action}
            </Box>
          ) : null}
        </Stack>
      ) : null}
      <Box sx={disablePadding ? undefined : { p: { xs: 2, md: 3 } }}>{children}</Box>
    </Paper>
  )
}
